'use client';

/* eslint-disable @next/next/no-img-element -- These local service visuals live inside a fixed, animated stage whose dimensions are controlled by GSAP. */
import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { HOME_SOLUTIONS } from './home-solutions-data';

const PIN_START_RATIO = 0.12;

function setActiveSolution(
  cards: HTMLElement[],
  navigationItems: HTMLElement[],
  activeIndex: number
) {
  cards.forEach((card, index) => {
    const active = index === activeIndex;
    card.classList.toggle('is-active', active);
    card.dataset.active = String(active);
    card.setAttribute('aria-hidden', String(!active));
  });

  navigationItems.forEach((item, index) => {
    const active = index === activeIndex;
    item.classList.toggle('is-active', active);
    item.dataset.active = String(active);
    if (active) item.setAttribute('aria-current', 'step');
    else item.removeAttribute('aria-current');
  });
}

export function HomeSolutionsScroll() {
  const sectionRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.registerPlugin(ScrollTrigger);

    const cards = Array.from(
      section.querySelectorAll<HTMLElement>('[data-solution-card]')
    );
    const navigationItems = Array.from(
      section.querySelectorAll<HTMLElement>('[data-solution-navigation]')
    );
    const stage = section.querySelector<HTMLElement>('[data-solution-stage]');
    if (!cards.length || !stage) return;

    let activeIndex = -1;
    const updateActiveSolution = (nextIndex: number) => {
      const clampedIndex = Math.max(0, Math.min(nextIndex, cards.length - 1));
      if (clampedIndex === activeIndex) return;
      activeIndex = clampedIndex;
      setActiveSolution(cards, navigationItems, activeIndex);
    };

    const context = gsap.context(() => {
      gsap.set(cards, {
        force3D: true,
        transformOrigin: '50% 50%',
        willChange: 'transform, opacity',
      });
      const getViewportHeight = () =>
        window.visualViewport?.height ?? window.innerHeight;
      // The stage pins 12% below the viewport top, so 88vh places the next
      // card exactly at the physical bottom edge of the device.
      const getPreviewOffset = () =>
        Math.max(
          cards[0].offsetHeight,
          getViewportHeight() * (1 - PIN_START_RATIO)
        );
      gsap.set(cards[0], { y: 0, scale: 1, opacity: 1, zIndex: 1 });
      gsap.set(cards.slice(1), {
        y: getPreviewOffset,
        scale: 1,
        opacity: 0,
        zIndex: 0,
      });
      updateActiveSolution(0);

      const timeline = gsap.timeline({
        defaults: { duration: 1, ease: 'none' },
        scrollTrigger: {
          id: 'home-solutions-scroll',
          trigger: section,
          start: `top ${PIN_START_RATIO * 100}%`,
          end: () => {
            const step = Math.max(640, getViewportHeight() * 0.9);
            return `+=${Math.round(step * (cards.length - 1))}`;
          },
          pin: true,
          // `#smooth-content main` establishes a fixed-position containing
          // block, so transform pinning is the stable option across browsers.
          pinType: 'transform',
          pinSpacing: true,
          anticipatePin: 1,
          scrub: 0.65,
          invalidateOnRefresh: true,
        },
      });

      timeline.eventCallback('onUpdate', () =>
        updateActiveSolution(
          Math.round(timeline.progress() * (cards.length - 1))
        )
      );

      cards.slice(1).forEach((card, index) => {
        timeline.to(cards[index], { opacity: 0, scale: 0.96 }, index).fromTo(
          card,
          { y: getPreviewOffset, opacity: 0, zIndex: index + 2 },
          {
            y: 0,
            opacity: 1,
            zIndex: index + 2,
            immediateRender: false,
          },
          index
        );
      });
    }, section);

    let refreshFrame: number | undefined;
    const requestRefresh = () => {
      if (refreshFrame !== undefined) window.cancelAnimationFrame(refreshFrame);
      refreshFrame = window.requestAnimationFrame(() =>
        ScrollTrigger.refresh()
      );
    };
    const resizeObserver = new ResizeObserver(requestRefresh);
    resizeObserver.observe(stage);
    window.addEventListener('orientationchange', requestRefresh);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('orientationchange', requestRefresh);
      if (refreshFrame !== undefined) window.cancelAnimationFrame(refreshFrame);
      context.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="home-solutions-scroll ship-smart-content-rail container relative isolate !mt-[clamp(44px,6vh,72px)] h-[340px]"
      aria-label="Ship Smart transportation solutions"
      data-solutions-mode="scroll"
    >
      <div className="home-solutions-scroll__layout grid grid-cols-[minmax(280px,0.36fr)_minmax(0,0.64fr)] items-start gap-[clamp(32px,4vw,72px)]">
        <ol className="home-solutions-scroll__navigation list-unstyled mb-0 self-start">
          {HOME_SOLUTIONS.map((solution) => (
            <li
              key={solution.title}
              data-solution-navigation
              className="home-solutions-scroll__navigation-item group flex min-h-[clamp(56px,6vh,72px)] items-center justify-between gap-5 border-b border-[var(--index-dark-line)] !py-3 text-[clamp(20px,1.5vw,29px)] font-semibold leading-[1.12] tracking-[-0.035em] text-[var(--index-dark-text)] transition-[padding,background-color] duration-200 first:border-t data-[active=true]:bg-[var(--index-dark-card)] data-[active=true]:!px-[18px]"
            >
              <span>{solution.title}</span>
              <svg
                aria-hidden="true"
                className="shrink-0 -translate-x-2 opacity-0 transition-[opacity,transform] duration-200 group-data-[active=true]:translate-x-0 group-data-[active=true]:opacity-100"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.1716 8.77806L0 8.77806V6.77807L12.1716 6.77807L6.80761 1.41412L8.22183 0L16 7.77806L8.22181 15.5562L6.80759 14.142L12.1716 8.77806Z"
                  fill="currentColor"
                />
              </svg>
            </li>
          ))}
        </ol>

        <div
          data-solution-stage
          className="home-solutions-scroll__stage relative h-screen max-h-screen overflow-hidden [contain:layout_paint]"
        >
          {HOME_SOLUTIONS.map((solution) => (
            <article
              key={solution.title}
              data-solution-card
              className="home-solutions-scroll__card pointer-events-none absolute inset-x-0 top-0 grid h-[340px] w-full grid-cols-[minmax(0,1.08fr)_minmax(270px,0.92fr)] items-stretch gap-[clamp(24px,2.5vw,40px)] overflow-hidden rounded-[20px] border border-[var(--index-dark-line)] bg-[var(--index-dark-card)] !p-[clamp(24px,2vw,32px)] text-[var(--index-dark-text)] [backface-visibility:hidden] data-[active=true]:pointer-events-auto"
            >
              <div className="home-solutions-scroll__copy flex min-w-0 flex-col justify-between">
                <img
                  src={solution.icon}
                  alt=""
                  width={64}
                  height={64}
                  decoding="async"
                  className="home-solutions-scroll__icon h-[clamp(48px,3.5vw,64px)] w-[clamp(48px,3.5vw,64px)] object-contain brightness-0 invert"
                />
                <p className="home-solutions-scroll__description !mt-[clamp(18px,3vh,32px)] mb-0 max-w-[24ch] !text-[clamp(19px,1.5vw,27px)] font-normal !leading-[1.25] tracking-[-0.025em] text-[var(--index-dark-text)]">
                  {solution.description}
                </p>
              </div>
              <div className="home-solutions-scroll__media h-full min-w-0 overflow-hidden rounded-xl">
                <img
                  src={solution.image}
                  alt=""
                  width={560}
                  height={420}
                  loading="lazy"
                  decoding="async"
                  className="!block !h-full !w-full !object-cover"
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
